import React, { useRef, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { ABOUT_BULLETS } from '../data/content';
import './About.css';

const CEO = { name: 'Kodees', role: 'Founder & CEO', color: '#e87c2a', g1: '#e87c2a', g2: '#f5a623' };

const SALES_TEAM = [
  { name: 'James Franklin', role: 'US Sales', color: '#9b6dff', g1: '#7c5cbf', g2: '#9b6dff' },
  { name: 'Jayakumar', role: 'Director – Sales & Operations', color: '#9b6dff', g1: '#7c5cbf', g2: '#9b6dff' },
  { name: 'Rathina Kumar', role: 'Director – Global Sales', color: '#9b6dff', g1: '#7c5cbf', g2: '#9b6dff' },
];

const TECH_MANAGERS = [
  { name: 'Rangaraj', role: 'Senior Director – PCB', color: '#4a90d9', g1: '#2563a8', g2: '#4a90d9', chain: ['Design Leads – PCB', 'Sr Engineers – PCB', 'Engineers'] },
  { name: 'Pushparaj', role: 'Director – PCB', color: '#06b6d4', g1: '#0284c7', g2: '#06b6d4', chain: ['Design Leads – Mechanical', 'Sr Engineers – Mechanical', 'Engineers'] },
  { name: 'Sadhish', role: 'Manager – IoT', color: '#4a90d9', g1: '#2563a8', g2: '#4a90d9', chain: ['Design Leads – IoT', 'Sr Engineers – IoT', 'Engineers'] },
];

const TEAM_DATA = {
  'kodees': {
    name: 'Kodees', role: 'Founder & CEO', team: 'Leadership',
    color: '#e87c2a', photo: '/images/team/kodees.jpeg',
    about: 'Kodees is the visionary founder and CEO of DWB Tech Solution, driving the company\'s mission to deliver world-class PCB design and engineering services. With over 22 years of industry experience inherited through the parent company established in 1994, Kodees leads a global team of engineers across India, USA, Japan, Vietnam and Malaysia.',
    expertise: ['Strategic Leadership', 'PCB Design Architecture', 'ASIC Verification', 'Global Operations', 'Business Development'],
    experience: '22+ Years',
    quote: 'Bring your requirement, walk away with a solution.',
  },
  'james-franklin': {
    name: 'James Franklin', role: 'US Sales', team: 'Sales Team',
    color: '#9b6dff', photo: '/images/team/james-franklin.jpeg',
    about: 'James Franklin manages DWB Tech\'s North American sales operations, bringing deep relationships with US-based semiconductor and electronics companies.',
    expertise: ['US Market Strategy', 'Semiconductor Sales', 'ATE & PCB Solutions', 'Key Account Management', 'Technical Sales'],
    experience: '30+ Years',
    quote: 'Delivering engineering excellence from coast to coast.',
  },
  'jayakumar': {
    name: 'Jayakumar', role: 'Director – Sales & Operations', team: 'Sales Team',
    color: '#9b6dff', photo: '/images/team/jayakumar.jpeg',
    about: 'Jayakumar oversees DWB Tech\'s sales and operational alignment, ensuring seamless delivery from client acquisition through project completion.',
    expertise: ['Sales Operations', 'Project Management', 'Process Optimization', 'Team Leadership', 'Client Delivery'],
    experience: '27+ Years',
    quote: 'Operational excellence is the backbone of every successful delivery.',
  },
  'rathina-kumar': {
    name: 'Rathina Kumar', role: 'Director – Global Sales', team: 'Sales Team',
    color: '#9b6dff', photo: '/images/team/rathina-kumar.jpeg',
    about: 'Rathina Kumar drives DWB Tech\'s sales initiatives with a focus on expanding the client base across key verticals.',
    expertise: ['Sales Strategy', 'Vertical Markets', 'Technical Proposals', 'Revenue Growth', 'Market Expansion'],
    experience: '20+ Years',
    quote: 'Every client relationship is built on trust, transparency, and results.',
  },
  'rangaraj': {
    name: 'Rangaraj', role: 'Senior Director – PCB Design', team: 'Technical Team',
    color: '#4a90d9', photo: '/images/team/Rangaraj.jpg',
    about: 'Rangaraj leads DWB Tech\'s PCB Design division with deep expertise in high-speed, multilayer, and ATE board design.',
    expertise: ['High-Speed PCB Design', 'ATE Board Design', 'Signal Integrity', 'Multilayer Stackup', 'Design Rule Check'],
    experience: '20+ Years',
    quote: 'Precision in every trace, perfection in every layer.',
  },
  'pushparaj': {
    name: 'Pushparaj', role: 'Director – PCB', team: 'Technical Team',
    color: '#06b6d4', photo: '/images/team/pushparaj.png',
    about: 'Pushparaj heads the PCB Engineering division at DWB Tech, overseeing PCB design, manufacturing, and integration.',
    expertise: ['PCB Design', 'PCB Manufacturing', 'CAD/CAM', 'PCB Integration', 'Manufacturing DFM'],
    experience: '20+ Years',
    quote: 'Where hardware meets precision — that\'s where we excel.',
  },
  'sadhish': {
    name: 'Sadhish', role: 'Manager – IoT', team: 'Technical Team',
    color: '#4a90d9', photo: '/images/team/sadhish.jpeg',
    about: 'Sadhish leads DWB Tech\'s IoT division, specializing in connected device design, firmware development, and embedded systems.',
    expertise: ['IoT Architecture', 'Embedded Systems', 'Firmware Development', 'Wireless Protocols', 'Cloud Integration'],
    experience: '21+ Years',
    quote: 'Connecting the physical world to the digital future, one device at a time.',
  },
};

/* ── Helper: name → slug ── */
function toSlug(name) {
  return name.toLowerCase().replace(/\s+/g, '-').replace(/[^a-z0-9-]/g, '');
}

/* ── Premium Avatar ── */
let _uid = 0;
function Avatar({ g1, g2, size = 48, isCeo = false }) {
  const id = `av${++_uid}`;
  return (
    <svg width={size} height={size} viewBox="0 0 60 60" fill="none" xmlns="http://www.w3.org/2000/svg">
      <defs>
        <radialGradient id={`bg${id}`} cx="50%" cy="50%" r="50%">
          <stop offset="0%" stopColor={g2} stopOpacity="0.25" />
          <stop offset="100%" stopColor={g1} stopOpacity="0.05" />
        </radialGradient>
        <linearGradient id={`body${id}`} x1="0" y1="0" x2="1" y2="1">
          <stop offset="0%" stopColor={g1} />
          <stop offset="100%" stopColor={g2} />
        </linearGradient>
      </defs>
      <circle cx="30" cy="30" r="29" fill={`url(#bg${id})`} stroke={g2} strokeWidth="1" strokeOpacity="0.4" />
      {isCeo && <circle cx="30" cy="30" r="29" fill="none" stroke={g2} strokeWidth="1.5" strokeOpacity="0.6" strokeDasharray="4 3" />}
      <circle cx="30" cy="22" r="10" fill={`url(#body${id})`} fillOpacity="0.9" />
      <ellipse cx="30" cy="46" rx="14" ry="10" fill={`url(#body${id})`} fillOpacity="0.8" />
      <path d="M25 35 L30 41 L35 35" stroke="white" strokeWidth="1.5" strokeOpacity="0.6" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      <rect x="28.5" y="37" width="3" height="8" rx="1" fill="white" fillOpacity="0.3" />
    </svg>
  );
}

function MemberPanel({ member }) {
  if (!member) return null;
  return (
    <div className="org-member-panel" style={{ '--mc': member.color }}>
      <div className="org-panel-left">
        {member.photo ? (
          <img src={member.photo} alt={member.name} className="org-panel-photo" />
        ) : (
          <div className="org-panel-photo-placeholder" style={{ background: `${member.color}22` }}>
            <span style={{ fontSize: 48 }}>👤</span>
          </div>
        )}
        <div className="org-panel-stats">
          <div className="org-panel-stat">
            <div className="org-panel-stat-val" style={{ color: member.color }}>{member.experience}</div>
            <div className="org-panel-stat-label">Experience</div>
          </div>



        </div>
      </div>
      <div className="org-panel-right">
        <div className="org-panel-role" style={{ color: member.color }}>{member.role}</div>
        <h3 className="org-panel-name">{member.name}</h3>
        <div className="org-panel-quote" style={{ borderColor: member.color }}>
          "{member.quote}"
        </div>
        <p className="org-panel-about">{member.about}</p>
        <div className="org-panel-expertise">
          {member.expertise.map((s, i) => (
            <span key={i} className="org-panel-skill" style={{ borderColor: `${member.color}44`, color: member.color }}>
              {s}
            </span>
          ))}
        </div>

      </div>
    </div>
  );
}

/* ── Org Node ── */
function OrgNode({ name, role, color, g1, g2, size = 'md', delay = 0 }) {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);
  const navigate = useNavigate();
  const slug = toSlug(name);

  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setVisible(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);

  const avSize = size === 'lg' ? 44 : size === 'sm' ? 34 : 40;
  const isCeo = size === 'lg';

  return (
    <div
      ref={ref}
      className={`org-node org-node--${size}${visible ? ' org-node--visible' : ''}`}
      style={{ '--nc': color, '--g1': g1, '--g2': g2, transitionDelay: `${delay}s`, cursor: 'pointer' }}
      onClick={() => navigate('/teammember', { state: { slug } })}
      title={`View ${name}'s profile`}
    >
      <div className="org-node-bg" />
      <div className="org-node-border-anim" />
      <div className="org-node-avatar">
        <Avatar g1={g1} g2={g2} size={avSize} isCeo={isCeo} />
        {isCeo && <div className="org-ceo-ring" />}
      </div>
      <div className="org-node-name">{name}</div>
      <div className="org-node-role">{role}</div>
      <div className="org-node-glow" />
      <div className="org-node-scan" />
      {/* View profile hint */}
      <div className="org-node-hint">View Profile →</div>
    </div>
  );
}

/* ── Chain Pill ── */
function ChainBox({ label, delay = 0 }) {
  const [v, setV] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const obs = new IntersectionObserver(([e]) => { if (e.isIntersecting) setV(true); }, { threshold: 0.05 });
    if (ref.current) obs.observe(ref.current);
    return () => obs.disconnect();
  }, []);
  return (
    <div ref={ref} className={`org-chain-box${v ? ' org-chain-box--visible' : ''}`} style={{ transitionDelay: `${delay}s` }}>
      <span className="org-chain-dot" />
      {label}
    </div>
  );
}

function Arrow({ color = '#4a90d9' }) {
  return (
    <div className="org-arrow">
      <svg width="30" height="14" viewBox="0 0 30 14" fill="none">
        <line x1="0" y1="7" x2="22" y2="7" stroke={color} strokeWidth="1.5" strokeDasharray="3 2" />
        <path d="M20 3L27 7L20 11" stroke={color} strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" fill="none" />
      </svg>
    </div>
  );
}

export default function About() {
  const [expandedSlug, setExpandedSlug] = useState(null);

  const toggleMember = (slug) => {
    setExpandedSlug(prev => prev === slug ? null : slug);
  };

  return (
    <div className="about-page">

      {/* ── Hero Banner ── */}
      <div className="about-hero">
        <div className="about-hero-bg" />
        <div className="about-hero-overlay" />
        <svg className="about-hero-svg" viewBox="0 0 1440 420" preserveAspectRatio="xMidYMid slice" xmlns="http://www.w3.org/2000/svg">
          <line x1="800" y1="0" x2="800" y2="420" stroke="rgba(74,144,217,0.08)" strokeWidth="1" />
          <line x1="900" y1="0" x2="900" y2="420" stroke="rgba(74,144,217,0.06)" strokeWidth="1" />
          <line x1="0" y1="150" x2="1440" y2="150" stroke="rgba(74,144,217,0.06)" strokeWidth="1" />
          <line x1="0" y1="280" x2="1440" y2="280" stroke="rgba(74,144,217,0.04)" strokeWidth="1" />
          {[[850, 80], [950, 150], [1050, 80], [1150, 200], [1050, 280], [950, 350], [1200, 300], [1300, 150], [1380, 80]].map(([x, y], i) => (
            <g key={i}>
              <circle cx={x} cy={y} r="5" fill="rgba(74,144,217,0.25)" stroke="rgba(74,144,217,0.4)" strokeWidth="1"
                style={{ animation: `nodePulse 2s ease ${i * 0.25}s infinite alternate` }} />
              <circle cx={x} cy={y} r="12" fill="none" stroke="rgba(74,144,217,0.1)" strokeWidth="1" />
            </g>
          ))}
          <rect x="980" y="170" width="140" height="90" rx="6" fill="rgba(26,60,94,0.4)" stroke="rgba(74,144,217,0.3)" strokeWidth="1.5" />
          <rect x="990" y="180" width="120" height="70" rx="4" fill="rgba(74,144,217,0.08)" />
          <text x="1050" y="220" textAnchor="middle" fontSize="11" fill="rgba(74,144,217,0.7)" fontFamily="monospace" fontWeight="bold">DWB TECH</text>
          <text x="1050" y="235" textAnchor="middle" fontSize="8" fill="rgba(74,144,217,0.4)" fontFamily="monospace">ASIC · PCB · RF</text>
        </svg>
        <div className="about-hero-content">
          <div className="about-hero-tag"> About DWB Tech</div>
          <h1 className="about-hero-title">Engineering the Future<br /><span className="about-hero-accent">of Electronics</span></h1>
          <p className="about-hero-sub">Since 1994 — Precision PCB Design, ASIC Verification & Global Engineering Excellence</p>
          <div className="about-hero-stats">
            {[[new Date().getFullYear() - 1994 + '+', 'Years Experience'], ['6500+', 'Projects Delivered'], ['50+', 'Global Clients'], ['24/7', 'Support']].map(([val, label]) => (
              <div key={label} className="about-hero-stat">
                <span className="about-hero-stat-val">{val}</span>
                <span className="about-hero-stat-label">{label}</span>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* ── About Section ── */}
      <section id="about" className="about-section">
        <div className="about-inner">
          <div className="about-visual reveal-left">
            <div className="about-card-main">
              <div className="about-card-img-clip">
                <img src="https://images.unsplash.com/photo-1518770660439-4636190af475?w=800&q=80" alt="DWB Tech PCB" className="about-img"
                  onError={e => { e.target.src = "https://images.unsplash.com/photo-1581092921461-eab62e97a780?w=800&q=80"; }} />
              </div>
              <div className="about-card-side">
                <div className="about-card-icon">🔬</div>
                <div className="about-card-text">
                  <div className="about-card-title">World-class Design</div>
                  <div className="about-card-desc">Multilayer & high-speed PCBs</div>
                </div>
              </div>
              <div className="about-badge-float">
                <span className="about-badge-icon">⚡</span>
                <div>
                  <div className="about-badge-val">Since 1994</div>
                  <div className="about-badge-sub">{new Date().getFullYear() - 1994}+ years of excellence</div>
                </div>
              </div>
            </div>
            <div className="about-ring" />
          </div>
          <div className="about-text reveal-right">
            <div className="section-tag"> About DWB Tech</div>
            <h2 className="about-heading">Redefining The Foundation Of{' '}<span className="gradient-text">Semiconductor Technology</span></h2>
            <p className="about-desc">DWB builds on a strong legacy of engineering excellence through its parent company established in 1994. DWB Tech Solution is a reputed global industry leader offering cutting-edge designing, manufacturing, assembling, firmware developing and ASIC verification worldwide.</p>
            <ul className="about-bullets">
              {ABOUT_BULLETS.map((b, i) => (
                <li key={i} className="about-bullet">
                  <span className="about-bullet-icon">✓</span>
                  <span>{b}</span>
                </li>
              ))}
            </ul>
            <div className="about-pills">
              {['Quality', 'Philosophy', 'Tradition'].map(t => <div key={t} className="about-pill">{t}</div>)}
            </div>

          </div>
        </div>
      </section>

      {/* ══ TEAM SECTION ══ */}
      <section className="org-section">
        <div className="org-bg-grid" />
        <div className="org-bg-orb org-bg-orb--1" />
        <div className="org-bg-orb org-bg-orb--2" />

        <div className="org-inner">
          <div className="org-header">
            <div className="org-header-tag"> Leadership Structure</div>
            <h2 className="org-title">Meet Our Team</h2>
            <p className="org-subtitle">The people behind DWB Tech's global engineering excellence</p>
          </div>

          {/* CEO Featured Card */}
          <div
            className={`org-ceo-featured${expandedSlug === toSlug(CEO.name) ? ' org-ceo-featured--active' : ''}`}
            onClick={() => toggleMember(toSlug(CEO.name))}
          >
            <div className="org-ceo-avatar-wrap">
              <div className="org-ceo-avatar">
                {TEAM_DATA['kodees']?.photo ? (
                  <img
                    src={TEAM_DATA['kodees'].photo}
                    alt="Kodees"
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                      objectPosition: 'center 15%',
                      borderRadius: '50%',
                      transform: 'scale(1)',      /* remove zoom-out on mobile */
                      transformOrigin: 'center top',
                    }}
                    onError={e => { e.target.style.display = 'none'; }}
                  />
                ) : (
                  <Avatar g1={CEO.g1} g2={CEO.g2} size={90} isCeo={true} />
                )}
              </div>
              <div className="org-ceo-badge">CEO</div>
            </div>
            <div className="org-ceo-info">
              <h3 className="org-ceo-name">{CEO.name}</h3>
              <p className="org-ceo-role">{CEO.role}</p>
              <p className="org-ceo-bio">
                Leading DWB Tech's global engineering vision since 1994 — driving innovation
                across PCB design, ASIC verification, and semiconductor solutions worldwide.
              </p>
              <div className="org-ceo-tags">
                {['PCB Design', 'ASIC', 'Leadership', 'Global Operations'].map(t => (
                  <span key={t} className="org-ceo-tag">{t}</span>
                ))}
              </div>
            </div>
            <div className="org-expand-icon">{expandedSlug === toSlug(CEO.name) ? '▲' : '▼'}</div>
          </div>

          {/* CEO Expanded Panel */}
          {expandedSlug === toSlug(CEO.name) && (
            <MemberPanel member={TEAM_DATA[toSlug(CEO.name)]} />
          )}

          {/* Sales Team */}
          <div className="org-team-section">
            <div className="org-team-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9b6dff" strokeWidth="2.2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Sales Team
            </div>
            <div className="org-members-grid">
              {SALES_TEAM.map((m) => (
                <React.Fragment key={m.name}>
                  <div
                    className={`org-member-card${expandedSlug === toSlug(m.name) ? ' org-member-card--active' : ''}`}
                    style={{ '--nc': m.color }}
                    onClick={() => toggleMember(toSlug(m.name))}
                  >
                    <div className="org-member-avatar">
                      {TEAM_DATA[toSlug(m.name)]?.photo ? (
                        <img
                          src={TEAM_DATA[toSlug(m.name)].photo}
                          alt={m.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            objectPosition: toSlug(m.name) === 'jayakumar' ? 'center top'
                              : toSlug(m.name) === 'rathina-kumar' ? 'center center'
                                : toSlug(m.name) === 'rangaraj' ? 'center top'
                                  : 'center top',
                            transform: toSlug(m.name) === 'rathina-kumar' ? 'scale(0.85)'
                              : toSlug(m.name) === 'rangaraj' ? 'scale(0.85)'
                                : 'scale(1)',
                            transformOrigin: 'center center',
                          }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Avatar g1={m.g1} g2={m.g2} size={56} />
                      )}
                    </div>
                    <div className="org-member-info">
                      <div className="org-member-name">{m.name}</div>
                      <div className="org-member-role">{m.role}</div>
                    </div>
                    <div className="org-member-arrow">
                      {expandedSlug === toSlug(m.name) ? '▲' : '▼'}
                    </div>
                  </div>
                  {expandedSlug === toSlug(m.name) && (
                    <div className="org-member-panel-wrapper">
                      <MemberPanel member={TEAM_DATA[toSlug(m.name)]} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

          {/* Technical Team */}
          <div className="org-team-section">
            <div className="org-team-label">
              <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#9b6dff" strokeWidth="2.2">
                <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
                <circle cx="9" cy="7" r="4" />
                <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
                <path d="M16 3.13a4 4 0 0 1 0 7.75" />
              </svg>
              Technical Team
            </div>
            <div className="org-members-grid">
              {TECH_MANAGERS.map((m) => (
                <React.Fragment key={m.name}>
                  <div
                    className={`org-member-card${expandedSlug === toSlug(m.name) ? ' org-member-card--active' : ''}`}
                    style={{ '--nc': m.color }}
                    onClick={() => toggleMember(toSlug(m.name))}
                  >
                    <div className="org-member-avatar">
                      {TEAM_DATA[toSlug(m.name)]?.photo ? (
                        <img
                          src={TEAM_DATA[toSlug(m.name)].photo}
                          alt={m.name}
                          style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            borderRadius: '50%',
                            objectPosition: toSlug(m.name) === 'rangaraj' ? 'center 20%'
                              : toSlug(m.name) === 'sadhish' ? 'center top'
                                : 'center 15%',
                            transform: toSlug(m.name) === 'rangaraj' ? 'scale(0.9)' : 'scale(1)',
                            transformOrigin: 'center center',
                          }}
                          onError={e => { e.target.style.display = 'none'; }}
                        />
                      ) : (
                        <Avatar g1={m.g1} g2={m.g2} size={56} />
                      )}
                    </div>
                    <div className="org-member-info">
                      <div className="org-member-name">{m.name}</div>
                      <div className="org-member-role">{m.role}</div>
                    </div>
                    <div className="org-member-arrow">
                      {expandedSlug === toSlug(m.name) ? '▲' : '▼'}
                    </div>
                  </div>
                  {expandedSlug === toSlug(m.name) && (
                    <div className="org-member-panel-wrapper">
                      <MemberPanel member={TEAM_DATA[toSlug(m.name)]} />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

        </div>
      </section>
    </div>
  );
}